import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  AlertCircle,
  ArrowUp,
  BellRing,
  MessageSquare,
  RefreshCw,
  SlidersHorizontal,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigationType, useSearchParams } from "react-router"

import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { PageNav, ResizeHandle } from "@/components"
import { LoadingPage, OnboardingEmptyState } from "@/components/System"
import { DEFAULT_LOADING_MESSAGE } from "@/constants"
import {
  agentResponseToAgent,
  useGetAgentsApi,
  useGetThreadByHistoryApi,
  useGetThreadsApi,
  type ThreadStatus,
  type ThreadSummaryResponse,
} from "@/hooks/api"
import {
  useAdaptivePolling,
  useDrawerResize,
  useToast,
  useWindowSize,
} from "@/hooks"
import { FetchStatus } from "@/hooks/useFetch"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { useStore } from "@/store"
import type { Agent, Workspace } from "@/types"

import {
  AgentSelect,
  ExternalUserIdSelect,
  FeedbackSelect,
  HumanModeSelect,
  SourceSelect,
  SortToggle,
  type FeedbackFilterValue,
  type HumanModeFilterValue,
  type SourceFilterValue,
} from "../HistoryList/components"
import {
  clearHistoryFilterParams,
  HISTORY_DETAIL_WIDTH_STORAGE_KEY,
  hasActiveHistoryFilters,
  isHistorySourceFilterValue,
  isHistoryIdentityScopeMasked,
  sanitizeMaskedHistoryIdentityParams,
  syncWorkspaceScopedHistoryParams,
} from "../HistoryList"
import ThreadDetail from "./ThreadDetail"
import ThreadListItem from "./ThreadListItem"
import { CustomerSearch } from "../CustomerSearch"
import ThreadStatusSelect, {
  type ThreadStatusFilterValue,
} from "./ThreadStatusSelect"
import {
  buildThreadListSearchParams,
  canonicalizeHistorySelection,
  clearThreadSelection,
  createThreadListPageSnapshot,
  DEFAULT_THREAD_ORDER,
  getAnchorHistoryId,
  getLegacyHistoryId,
  getSelectedThreadKey,
  getThreadListPageStructureDiff,
  getThreadListPageSnapshotKey,
  mergeThreadListPageSnapshotRows,
  mergeThreadSummaryVersion,
  patchThreadListPageSnapshot,
  selectThread,
  syncSelectedThreadParams,
  THREAD_PAGE_LIMIT,
  type ThreadListPageSnapshot,
  type ThreadOrderBy,
  type ThreadSummaryVersion,
} from "./threadListState"

const DEFAULT_FEEDBACK_FILTER: FeedbackFilterValue = "no_filter"
const DEFAULT_HUMAN_MODE_FILTER: HumanModeFilterValue = "no_filter"
const DEFAULT_SOURCE_FILTER: SourceFilterValue = "all"
const AGENT_FILTER_ALL = "all"
const AGENT_FILTER_NONE = "none"
const DETAIL_MIN_WIDTH = 420
const DETAIL_COMPACT_MIN_WIDTH = 320
const DETAIL_MAX_WIDTH = 920
const LIST_MIN_WIDTH = 360
const DETAIL_DEFAULT_WIDTH = 680
const SPLIT_PANE_BORDER_PX = 2
const THREAD_LIST_POLL_INTERVAL_MS = 30_000
const THREAD_LIST_INVALIDATION_DEBOUNCE_MS = 300
const THREAD_LIST_TOP_THRESHOLD_PX = 8
const THREAD_POLL_JITTER_RANGE = { min: 0.9, max: 1 } as const

interface PendingThreadListSnapshot {
  queryKey: string
  snapshot: ThreadListPageSnapshot
}

interface ThreadListAnchorSnapshot {
  threadId: string
  top: number
}

const clampDetailWidth = (
  value: number,
  maxWidth: number,
  minWidth = DETAIL_MIN_WIDTH
) => Math.max(minWidth, Math.min(maxWidth, value))

const getInitialDetailWidth = () => {
  if (typeof window === "undefined") return DETAIL_DEFAULT_WIDTH
  const value = Number.parseInt(
    window.localStorage.getItem(HISTORY_DETAIL_WIDTH_STORAGE_KEY) || "",
    10
  )
  return Number.isNaN(value) ? DETAIL_DEFAULT_WIDTH : value
}

export interface ThreadListProps {
  workspace: Workspace
}

const ThreadList = ({ workspace }: ThreadListProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const { isMobile, width: viewportWidth } = useWindowSize()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigationType = useNavigationType()
  const currentOrganization = useStore((state) => state.currentOrganization)
  const organizationId = currentOrganization?.id || ""
  const page = Number.parseInt(searchParams.get("page") || "1", 10)
  const agentFilterId = searchParams.get("aid") || ""
  const externalUserId = searchParams.get("pem") || ""
  const clientUserId = searchParams.get("cuid") || ""
  const customerSearch = searchParams.get("customer")?.trim() || ""
  const orderBy =
    (searchParams.get("ob") as ThreadOrderBy) || DEFAULT_THREAD_ORDER
  const feedbackFilter =
    (searchParams.get("fb") as FeedbackFilterValue) || DEFAULT_FEEDBACK_FILTER
  const humanModeFilter =
    (searchParams.get("hitl") as HumanModeFilterValue) ||
    DEFAULT_HUMAN_MODE_FILTER
  const rawSourceFilter = searchParams.get("src") || searchParams.get("ch")
  const sourceFilter = isHistorySourceFilterValue(rawSourceFilter)
    ? rawSourceFilter
    : DEFAULT_SOURCE_FILTER
  const rawThreadStatusFilter = searchParams.get("status")
  const statusFilter: ThreadStatusFilterValue =
    rawThreadStatusFilter === "pending" ? "pending" : "all"
  const selectedThreadKey = getSelectedThreadKey(searchParams)
  const anchorHistoryId = getAnchorHistoryId(searchParams)
  const legacyHistoryId = getLegacyHistoryId(searchParams)
  const identityMasked = isHistoryIdentityScopeMasked([workspace], workspace.id)

  const [agents, setAgents] = useState<Agent[]>([])
  const [threads, setThreads] = useState<ThreadSummaryResponse[]>()
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [pendingSnapshot, setPendingSnapshot] =
    useState<PendingThreadListSnapshot | null>(null)
  const [resolvingHistory, setResolvingHistory] = useState(false)
  const [detailPaneWidth, setDetailPaneWidth] = useState(getInitialDetailWidth)
  const [crmOpen, setCrmOpen] = useState(false)
  const [splitPaneWidth, setSplitPaneWidth] = useState<number>()
  const readIntentSequenceRef = useRef(selectedThreadKey ? 1 : 0)
  const [readIntent, setReadIntent] = useState<{
    threadKey: string
    token: number
  } | null>(() =>
    selectedThreadKey ? { threadKey: selectedThreadKey, token: 1 } : null
  )
  const previousSelectedThreadKeyRef = useRef(selectedThreadKey)
  const splitPaneRef = useRef<HTMLDivElement | null>(null)
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  const listTopRef = useRef<HTMLDivElement | null>(null)
  const requestGenerationRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const invalidationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const appliedSnapshotRef = useRef<ThreadListPageSnapshot | null>(null)
  const pendingSnapshotRef = useRef<PendingThreadListSnapshot | null>(null)
  const anchorSnapshotRef = useRef<ThreadListAnchorSnapshot | null>(null)
  const [unseenLatestThread, setUnseenLatestThread] = useState<{
    queryKey: string
    id: string
  } | null>(null)
  const isListAtTopRef = useRef(true)
  const summaryRevisionRef = useRef(0)
  const summaryVersionsRef = useRef(new Map<string, ThreadSummaryVersion>())
  const loadedQueryRef = useRef<string | null>(null)
  const autoSelectionQueryRef = useRef<string | null>(null)
  const skipNextAutoSelectionRef = useRef(false)
  const scrollAfterLoadRef = useRef(false)

  const getThreadSummaryRevision = useCallback(
    () => ++summaryRevisionRef.current,
    []
  )

  const reconcileThreadSummary = useCallback(
    (id: string, patch: Partial<ThreadSummaryResponse>, revision: number) => {
      const version = mergeThreadSummaryVersion(
        summaryVersionsRef.current.get(id),
        patch,
        revision
      )
      summaryVersionsRef.current.set(id, version)
      return version.summary
    },
    []
  )

  useEffect(() => {
    // Keep only displayed, pending, and selected rows; a long-lived inbox must
    // not retain every conversation it has ever received.
    const retainedIds = new Set([
      ...(threads ?? []).map((thread) => thread.id),
      ...(pendingSnapshot?.snapshot.items ?? []).map((thread) => thread.id),
      selectedThreadKey,
    ])
    for (const id of summaryVersionsRef.current.keys()) {
      if (!retainedIds.has(id)) summaryVersionsRef.current.delete(id)
    }
  }, [pendingSnapshot, selectedThreadKey, threads])

  const issueReadIntent = useCallback((threadKey: string) => {
    readIntentSequenceRef.current += 1
    setReadIntent({
      threadKey,
      token: readIntentSequenceRef.current,
    })
  }, [])

  const listSearchParams = useMemo(
    () =>
      buildThreadListSearchParams({
        page,
        agentId: agentFilterId,
        externalUserId: identityMasked && !clientUserId ? "" : externalUserId,
        clientUserId,
        customerSearch: identityMasked ? "" : customerSearch,
        orderBy,
        feedbackFilter,
        humanModeFilter,
        sourceFilter,
        statusFilter,
      }),
    [
      agentFilterId,
      clientUserId,
      customerSearch,
      externalUserId,
      feedbackFilter,
      humanModeFilter,
      identityMasked,
      orderBy,
      page,
      sourceFilter,
      statusFilter,
    ]
  )
  const listQueryKey = useMemo(
    () => `${organizationId}:${workspace.id}:${listSearchParams.toString()}`,
    [listSearchParams, organizationId, workspace.id]
  )
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (
      agentFilterId &&
      agentFilterId !== AGENT_FILTER_ALL &&
      agentFilterId !== AGENT_FILTER_NONE
    )
      count += 1
    if (externalUserId || clientUserId) count += 1
    if (customerSearch) count += 1
    if (feedbackFilter !== DEFAULT_FEEDBACK_FILTER) count += 1
    if (humanModeFilter !== DEFAULT_HUMAN_MODE_FILTER) count += 1
    if (sourceFilter !== DEFAULT_SOURCE_FILTER) count += 1
    if (statusFilter === "pending") count += 1
    if (orderBy !== DEFAULT_THREAD_ORDER) count += 1
    return count
  }, [
    agentFilterId,
    clientUserId,
    customerSearch,
    externalUserId,
    feedbackFilter,
    humanModeFilter,
    orderBy,
    sourceFilter,
    statusFilter,
  ])
  const isLiveThreadListQuery = page === 1 && orderBy === DEFAULT_THREAD_ORDER

  const {
    data: threadsResponse,
    error: threadsError,
    status: threadsStatus,
    fetchFn: getThreads,
  } = useGetThreadsApi(organizationId, workspace.id)
  const { fetchFn: pollThreads } = useGetThreadsApi(
    organizationId,
    workspace.id
  )
  const {
    data: agentsResponse,
    error: agentsError,
    status: agentsStatus,
    fetchFn: getAgents,
  } = useGetAgentsApi()
  const { error: resolveHistoryError, fetchFn: resolveHistory } =
    useGetThreadByHistoryApi(
      organizationId,
      workspace.id,
      legacyHistoryId ?? -1
    )

  const applyThreadListSnapshot = useCallback(
    (snapshot: ThreadListPageSnapshot) => {
      appliedSnapshotRef.current = snapshot
      setThreads(snapshot.items)
      setTotalPages(snapshot.totalPages)
      setTotalRecords(snapshot.totalRecords)
    },
    []
  )

  const clearPendingSnapshot = useCallback(() => {
    pendingSnapshotRef.current = null
    setPendingSnapshot(null)
  }, [])

  const captureListAnchor = useCallback(() => {
    const container = listScrollRef.current
    if (!container) return
    const viewportTop = isMobile ? 0 : container.getBoundingClientRect().top
    const rows = Array.from(
      container.querySelectorAll<HTMLElement>("[data-thread-id]")
    )
    const anchor = rows.find(
      (row) => row.getBoundingClientRect().bottom > viewportTop
    )
    if (!anchor?.dataset.threadId) return
    anchorSnapshotRef.current = {
      threadId: anchor.dataset.threadId,
      top: anchor.getBoundingClientRect().top,
    }
  }, [isMobile])

  useLayoutEffect(() => {
    const anchorSnapshot = anchorSnapshotRef.current
    const container = listScrollRef.current
    if (!anchorSnapshot || !container) return
    anchorSnapshotRef.current = null
    const anchor = Array.from(
      container.querySelectorAll<HTMLElement>("[data-thread-id]")
    ).find((row) => row.dataset.threadId === anchorSnapshot.threadId)
    if (!anchor) return
    const delta = anchor.getBoundingClientRect().top - anchorSnapshot.top
    if (delta === 0) return
    if (isMobile) {
      window.scrollBy?.({ top: delta, behavior: "auto" })
    } else {
      container.scrollTop += delta
    }
  }, [isMobile, threads, pendingSnapshot])

  const scrollListToTop = useCallback(() => {
    anchorSnapshotRef.current = null
    isListAtTopRef.current = true
    if (isMobile) {
      listTopRef.current?.scrollIntoView?.({ block: "start", behavior: "auto" })
    } else {
      listScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" })
    }
  }, [isMobile])

  useLayoutEffect(() => {
    if (!scrollAfterLoadRef.current || threads === undefined) return
    scrollAfterLoadRef.current = false
    scrollListToTop()
  }, [scrollListToTop, threads])

  const applyPendingSnapshot = useCallback(() => {
    const pending = pendingSnapshotRef.current
    if (pending?.queryKey !== listQueryKey) return
    clearPendingSnapshot()
    anchorSnapshotRef.current = null
    if (page > pending.snapshot.totalPages) {
      // A snapshot fetched for an invalid offset is not the target page's data.
      skipNextAutoSelectionRef.current = true
      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        params.set("page", pending.snapshot.totalPages.toString())
        return params
      })
      return
    }
    applyThreadListSnapshot(pending.snapshot)
  }, [
    applyThreadListSnapshot,
    clearPendingSnapshot,
    listQueryKey,
    page,
    setSearchParams,
  ])

  const isThreadRowVisible = useCallback(
    (id: string) => {
      const container = listScrollRef.current
      const row = Array.from(
        container?.querySelectorAll<HTMLElement>("[data-thread-id]") ?? []
      ).find((element) => element.dataset.threadId === id)
      if (!container || !row) return false
      const rect = row.getBoundingClientRect()
      const bounds = container.getBoundingClientRect()
      return (
        rect.bottom > Math.max(0, isMobile ? 0 : bounds.top) &&
        rect.top <
          Math.min(
            window.innerHeight,
            isMobile ? window.innerHeight : bounds.bottom
          )
      )
    },
    [isMobile]
  )

  const noticeLatestMessage = useCallback(
    (
      previous: ThreadSummaryResponse | undefined,
      incoming: ThreadSummaryResponse | undefined
    ) => {
      if (
        !isLiveThreadListQuery ||
        !previous ||
        !incoming ||
        previous.id !== incoming.id
      )
        return
      const changed =
        incoming.latest_message?.created_at !==
          previous.latest_message?.created_at ||
        incoming.latest_message_preview !== previous.latest_message_preview
      if (
        changed &&
        !isListAtTopRef.current &&
        !isThreadRowVisible(incoming.id)
      ) {
        setUnseenLatestThread({ queryKey: listQueryKey, id: incoming.id })
      }
    },
    [isLiveThreadListQuery, isThreadRowVisible, listQueryKey]
  )

  const updateListTopState = useCallback(() => {
    const container = listScrollRef.current
    if (!container) return
    const top = listTopRef.current?.getBoundingClientRect().top
    const isTopVisible =
      top !== undefined && top >= 0 && top < window.innerHeight
    const isAtTop =
      isTopVisible &&
      (isMobile || container.scrollTop <= THREAD_LIST_TOP_THRESHOLD_PX)
    isListAtTopRef.current = isAtTop
    if (
      isAtTop ||
      (unseenLatestThread && isThreadRowVisible(unseenLatestThread.id))
    )
      setUnseenLatestThread(null)
    if (isAtTop && isLiveThreadListQuery) applyPendingSnapshot()
  }, [
    applyPendingSnapshot,
    isLiveThreadListQuery,
    isMobile,
    isThreadRowVisible,
    unseenLatestThread,
  ])

  const isListLoading = threads === undefined
  const isListEmpty = threads?.length === 0
  useEffect(() => {
    const container = listScrollRef.current
    if (!container) return
    let frameId: number | null = null
    let disposed = false
    const scheduleMeasurement = () => {
      // A response arriving before the frame must not use the old top position
      // to reorder rows. The frame applies any deferred snapshot at the new top.
      isListAtTopRef.current = false
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(() => {
        frameId = null
        if (!disposed) updateListTopState()
      })
    }
    const handleScroll = (event: Event) => {
      const target = event.target
      if (
        target === event.currentTarget ||
        target === document ||
        (target instanceof Element && target.contains(container))
      )
        scheduleMeasurement()
    }
    updateListTopState()
    // One capture listener covers the list itself and ancestors that can move
    // it. Sibling detail/composer and nested row scrolling do not move the list.
    window.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    })
    window.addEventListener("resize", scheduleMeasurement)
    return () => {
      disposed = true
      if (frameId !== null) window.cancelAnimationFrame(frameId)
      window.removeEventListener("scroll", handleScroll, true)
      window.removeEventListener("resize", scheduleMeasurement)
    }
  }, [
    isMobile,
    selectedThreadKey,
    updateListTopState,
    isListLoading,
    isListEmpty,
  ])

  const requestThreads = useCallback(() => {
    if (!organizationId) return
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    const generation = ++requestGenerationRef.current
    const revision = getThreadSummaryRevision()
    loadedQueryRef.current = null
    setUnseenLatestThread(null)
    appliedSnapshotRef.current = null
    clearPendingSnapshot()
    setThreads(undefined)
    anchorSnapshotRef.current = null
    isListAtTopRef.current = true
    scrollAfterLoadRef.current = true

    void getThreads({
      cache: "no-store",
      searchParams: new URLSearchParams(listSearchParams),
      signal: controller.signal,
      callbackFn: (response) => {
        if (generation !== requestGenerationRef.current || !response?.data)
          return
        const snapshot = createThreadListPageSnapshot({
          items: response.data.map(
            (thread) =>
              reconcileThreadSummary(
                thread.id,
                thread,
                revision
              ) as ThreadSummaryResponse
          ),
          totalPages: response.pagination?.total_pages || 1,
          totalRecords: response.pagination?.total_records || 0,
        })
        loadedQueryRef.current = listQueryKey
        applyThreadListSnapshot(snapshot)
        if (page > snapshot.totalPages) {
          const pending = { queryKey: listQueryKey, snapshot }
          pendingSnapshotRef.current = pending
          setPendingSnapshot(pending)
        } else {
          clearPendingSnapshot()
        }
      },
    }).catch(() => undefined)
  }, [
    applyThreadListSnapshot,
    clearPendingSnapshot,
    getThreads,
    getThreadSummaryRevision,
    reconcileThreadSummary,
    listSearchParams,
    listQueryKey,
    organizationId,
    page,
  ])

  useEffect(() => {
    requestThreads()
    return () => {
      requestGenerationRef.current += 1
      abortControllerRef.current?.abort()
      if (invalidationTimeoutRef.current !== null) {
        clearTimeout(invalidationTimeoutRef.current)
        invalidationTimeoutRef.current = null
      }
    }
  }, [requestThreads])

  const pollThreadList = useCallback(
    async ({ signal }: { signal: AbortSignal }) => {
      const revision = getThreadSummaryRevision()
      const startedAtGeneration = requestGenerationRef.current
      const response = await pollThreads({
        cache: "no-store",
        preserveData: true,
        searchParams: new URLSearchParams(listSearchParams),
        signal,
      })
      if (
        signal.aborted ||
        startedAtGeneration !== requestGenerationRef.current
      )
        return "unchanged" as const
      if (!response?.data)
        throw new Error("Thread list polling returned no data")

      const reconciledSnapshot = createThreadListPageSnapshot({
        items: response.data.map(
          (thread) =>
            reconcileThreadSummary(
              thread.id,
              thread,
              revision
            ) as ThreadSummaryResponse
        ),
        totalPages: response.pagination?.total_pages || 1,
        totalRecords: response.pagination?.total_records || 0,
      })
      const appliedSnapshot = appliedSnapshotRef.current
      if (!appliedSnapshot) return "unchanged" as const
      const structureDiff = getThreadListPageStructureDiff(
        appliedSnapshot,
        reconciledSnapshot
      )
      const canApplyPagination = page <= reconciledSnapshot.totalPages
      const mergedRowsSnapshot = mergeThreadListPageSnapshotRows(
        appliedSnapshot,
        reconciledSnapshot
      )
      const mergedAppliedSnapshot = canApplyPagination
        ? {
            ...mergedRowsSnapshot,
            totalPages: reconciledSnapshot.totalPages,
            totalRecords: reconciledSnapshot.totalRecords,
          }
        : mergedRowsSnapshot
      const rowsChanged =
        getThreadListPageSnapshotKey(mergedAppliedSnapshot) !==
        getThreadListPageSnapshotKey(appliedSnapshot)
      noticeLatestMessage(appliedSnapshot.items[0], reconciledSnapshot.items[0])
      const viewportStructureChanged =
        structureDiff.addedIds.length > 0 ||
        structureDiff.removedIds.length > 0 ||
        structureDiff.orderChanged
      const shouldDefer =
        !canApplyPagination ||
        (viewportStructureChanged &&
          (!isLiveThreadListQuery || !isListAtTopRef.current))

      if (!shouldDefer) {
        const nextDisplayed =
          isLiveThreadListQuery && isListAtTopRef.current
            ? reconciledSnapshot
            : mergedAppliedSnapshot
        const changed =
          getThreadListPageSnapshotKey(nextDisplayed) !==
          getThreadListPageSnapshotKey(appliedSnapshot)
        if (changed) {
          if (!isListAtTopRef.current) captureListAnchor()
          applyThreadListSnapshot(nextDisplayed)
        }
        if (!isListAtTopRef.current && pendingSnapshotRef.current && !changed)
          captureListAnchor()
        clearPendingSnapshot()
        return changed ? ("changed" as const) : ("unchanged" as const)
      }

      // Off the live top, synchronize summaries without moving page members.
      if (!isListAtTopRef.current) captureListAnchor()
      if (rowsChanged) applyThreadListSnapshot(mergedAppliedSnapshot)
      const currentPending = pendingSnapshotRef.current
      if (
        currentPending?.queryKey === listQueryKey &&
        getThreadListPageSnapshotKey(currentPending.snapshot) ===
          getThreadListPageSnapshotKey(reconciledSnapshot)
      ) {
        return rowsChanged ? ("changed" as const) : ("unchanged" as const)
      }
      const nextPending = {
        queryKey: listQueryKey,
        snapshot: reconciledSnapshot,
      }
      pendingSnapshotRef.current = nextPending
      setPendingSnapshot(nextPending)
      return "changed" as const
    },
    [
      applyThreadListSnapshot,
      captureListAnchor,
      clearPendingSnapshot,
      isLiveThreadListQuery,
      listQueryKey,
      listSearchParams,
      page,
      pollThreads,
      noticeLatestMessage,
      getThreadSummaryRevision,
      reconcileThreadSummary,
    ]
  )

  const {
    runNow: runThreadListPollNow,
    isPolling: isThreadListPolling,
    consecutiveFailures: threadListPollingFailures,
  } = useAdaptivePolling({
    enabled: Boolean(organizationId && threads !== undefined),
    scopeKey: listQueryKey,
    poll: pollThreadList,
    getDelayMs: () => THREAD_LIST_POLL_INTERVAL_MS,
    initialDelayMs: THREAD_LIST_POLL_INTERVAL_MS,
    jitterRange: THREAD_POLL_JITTER_RANGE,
  })

  const invalidateThreadList = useCallback(() => {
    if (appliedSnapshotRef.current) {
      if (invalidationTimeoutRef.current !== null) {
        clearTimeout(invalidationTimeoutRef.current)
      }
      invalidationTimeoutRef.current = setTimeout(() => {
        invalidationTimeoutRef.current = null
        runThreadListPollNow()
      }, THREAD_LIST_INVALIDATION_DEBOUNCE_MS)
      return
    }
    requestThreads()
  }, [requestThreads, runThreadListPollNow])

  useEffect(() => {
    const params = syncWorkspaceScopedHistoryParams(searchParams, workspace.id)
    if (params) setSearchParams(params, { replace: true })
  }, [searchParams, setSearchParams, workspace.id])

  useEffect(() => {
    const canonicalSource = searchParams.get("src")
    const legacySource = searchParams.get("ch")
    if (
      legacySource === null &&
      (canonicalSource === null || isHistorySourceFilterValue(canonicalSource))
    ) {
      return
    }
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current.toString())
        if (!isHistorySourceFilterValue(canonicalSource)) {
          if (isHistorySourceFilterValue(legacySource)) {
            params.set("src", legacySource)
          } else {
            params.delete("src")
            params.set("page", "1")
          }
        }
        params.delete("ch")
        return params
      },
      { replace: true }
    )
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (rawThreadStatusFilter === null || rawThreadStatusFilter === "pending") {
      return
    }
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current.toString())
        params.delete("status")
        params.set("page", "1")
        return params
      },
      { replace: true }
    )
  }, [rawThreadStatusFilter, setSearchParams])

  useEffect(() => {
    if (
      !identityMasked ||
      ((!externalUserId || externalUserId === "all") && !customerSearch)
    )
      return
    setSearchParams(
      (current) =>
        sanitizeMaskedHistoryIdentityParams(current, true) ?? current,
      { replace: true }
    )
  }, [externalUserId, customerSearch, identityMasked, setSearchParams])

  useEffect(() => {
    if (!organizationId) return
    getAgents({
      searchParams: new URLSearchParams({
        oid: organizationId,
        wid: workspace.id,
      }),
    })
  }, [getAgents, organizationId, workspace.id])

  useEffect(() => {
    if (agentsStatus === FetchStatus.Success && agentsResponse?.data) {
      setAgents(agentsResponse.data.map(agentResponseToAgent))
    } else if (agentsStatus === FetchStatus.Error) {
      setAgents([])
    }
  }, [agentsResponse, agentsStatus])

  useEffect(() => {
    if (legacyHistoryId === null || !organizationId) return
    let active = true
    setResolvingHistory(true)
    void resolveHistory({
      callbackFn: (response) => {
        if (!active || !response?.data) return
        issueReadIntent(response.data.id)
        setSearchParams(
          (current) =>
            canonicalizeHistorySelection(
              current,
              response.data!.id,
              legacyHistoryId
            ),
          { replace: true }
        )
      },
    })
      .catch(() => undefined)
      .finally(() => {
        if (active) setResolvingHistory(false)
      })
    return () => {
      active = false
    }
  }, [
    issueReadIntent,
    legacyHistoryId,
    organizationId,
    resolveHistory,
    setSearchParams,
  ])

  useEffect(() => {
    if (!resolveHistoryError) return
    alertToast({
      title: t("historyList.thread.historyResolveFailed"),
      description: getUserFacingApiErrorMessage(
        resolveHistoryError,
        t,
        t("historyList.thread.historyResolveFailedDescription")
      ),
    })
  }, [alertToast, resolveHistoryError, t])

  useEffect(() => {
    if (
      threads === undefined ||
      loadedQueryRef.current !== listQueryKey ||
      autoSelectionQueryRef.current === listQueryKey
    )
      return
    autoSelectionQueryRef.current = listQueryKey
    if (skipNextAutoSelectionRef.current) {
      skipNextAutoSelectionRef.current = false
      return
    }
    const next = syncSelectedThreadParams({
      searchParams,
      threads: appliedSnapshotRef.current?.items,
      isMobile,
      resolvingHistory,
    })
    if (next) {
      setReadIntent(null)
      setSearchParams(next, { replace: true })
    }
  }, [
    isMobile,
    listQueryKey,
    resolvingHistory,
    searchParams,
    setSearchParams,
    threads,
  ])

  useEffect(() => {
    const previousThreadKey = previousSelectedThreadKeyRef.current
    if (previousThreadKey === selectedThreadKey) return
    previousSelectedThreadKeyRef.current = selectedThreadKey
    if (!selectedThreadKey) {
      setReadIntent(null)
      return
    }
    if (navigationType === "POP") issueReadIntent(selectedThreadKey)
  }, [issueReadIntent, navigationType, selectedThreadKey])

  useEffect(() => {
    if (!threadsError) return
    alertToast({
      title: t("historyList.thread.loadFailed"),
      description: getUserFacingApiErrorMessage(
        threadsError,
        t,
        threadsResponse?.message ||
          t("historyList.thread.loadFailedDescription")
      ),
    })
  }, [alertToast, t, threadsError, threadsResponse?.message])

  const updateSearchParams = useCallback(
    (updates: {
      page?: number
      agentId?: string
      externalUserId?: string
      clientUserId?: string
      customerSearch?: string
      orderBy?: ThreadOrderBy
      feedbackFilter?: FeedbackFilterValue
      humanModeFilter?: HumanModeFilterValue
      sourceFilter?: SourceFilterValue
      statusFilter?: ThreadStatusFilterValue
    }) => {
      setSearchParams((current) => {
        const params = new URLSearchParams(current.toString())
        if (updates.page !== undefined) {
          params.set("page", updates.page.toString())
        }
        if (updates.agentId !== undefined) {
          if (updates.agentId) params.set("aid", updates.agentId)
          else params.delete("aid")
        }
        if (updates.externalUserId !== undefined) {
          const value = updates.externalUserId.trim()
          if (value) params.set("pem", value)
          else params.delete("pem")
        }
        if (updates.clientUserId !== undefined) {
          const value = updates.clientUserId.trim()
          if (value) params.set("cuid", value)
          else params.delete("cuid")
        }
        if (updates.customerSearch !== undefined) {
          const value = updates.customerSearch.trim()
          if (value) params.set("customer", value)
          else params.delete("customer")
          return clearThreadSelection(params)
        }
        if (updates.orderBy !== undefined) {
          if (updates.orderBy === DEFAULT_THREAD_ORDER) params.delete("ob")
          else params.set("ob", updates.orderBy)
        }
        if (updates.feedbackFilter !== undefined) {
          if (updates.feedbackFilter === DEFAULT_FEEDBACK_FILTER) {
            params.delete("fb")
          } else {
            params.set("fb", updates.feedbackFilter)
          }
        }
        if (updates.humanModeFilter !== undefined) {
          if (updates.humanModeFilter === DEFAULT_HUMAN_MODE_FILTER) {
            params.delete("hitl")
          } else {
            params.set("hitl", updates.humanModeFilter)
          }
        }
        if (updates.sourceFilter !== undefined) {
          params.delete("ch")
          if (updates.sourceFilter === DEFAULT_SOURCE_FILTER) {
            params.delete("src")
          } else {
            params.set("src", updates.sourceFilter)
          }
        }
        if (updates.statusFilter !== undefined) {
          if (updates.statusFilter === "pending") {
            params.set("status", "pending")
            return clearThreadSelection(params)
          } else {
            params.delete("status")
          }
        }
        return params
      })
    },
    [setSearchParams]
  )

  const handleSelectThread = useCallback(
    (thread: ThreadSummaryResponse) => {
      issueReadIntent(thread.id)
      setSearchParams((current) => selectThread(current, thread.id))
    },
    [issueReadIntent, setSearchParams]
  )

  const handleBack = useCallback(() => {
    setSearchParams((current) => clearThreadSelection(current))
  }, [setSearchParams])

  const handleClearAnchor = useCallback(() => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current.toString())
        params.delete("anchor_history_id")
        return params
      },
      { replace: true }
    )
  }, [setSearchParams])

  const patchThreadSummary = useCallback(
    (
      threadKey: string,
      patch: Partial<ThreadSummaryResponse>,
      revision = getThreadSummaryRevision()
    ) => {
      patch = reconcileThreadSummary(threadKey, patch, revision)
      if (!isListAtTopRef.current) captureListAnchor()
      const appliedSnapshot = appliedSnapshotRef.current
      if (appliedSnapshot) {
        const nextApplied = patchThreadListPageSnapshot(
          appliedSnapshot,
          threadKey,
          patch
        )
        noticeLatestMessage(appliedSnapshot.items[0], nextApplied.items[0])
        appliedSnapshotRef.current = nextApplied
        setThreads(nextApplied.items)
      } else {
        setThreads((current) =>
          current?.map((thread) =>
            thread.id === threadKey ? { ...thread, ...patch } : thread
          )
        )
      }

      const currentPendingSnapshot = pendingSnapshotRef.current
      if (currentPendingSnapshot?.queryKey !== listQueryKey) return
      const nextPendingSnapshot = {
        ...currentPendingSnapshot,
        snapshot: patchThreadListPageSnapshot(
          currentPendingSnapshot.snapshot,
          threadKey,
          patch
        ),
      }
      pendingSnapshotRef.current = nextPendingSnapshot
      setPendingSnapshot(nextPendingSnapshot)
    },
    [
      captureListAnchor,
      listQueryKey,
      noticeLatestMessage,
      getThreadSummaryRevision,
      reconcileThreadSummary,
    ]
  )

  const handleApplyManualSnapshot = useCallback(() => {
    setUnseenLatestThread(null)
    applyPendingSnapshot()
    scrollListToTop()
  }, [applyPendingSnapshot, scrollListToTop])

  const handleReadStateChanged = useCallback(
    (threadKey: string, isUnread: boolean) => {
      patchThreadSummary(threadKey, { is_unread: isUnread })
      // Reconcile the local patch without clearing list polling failures/backoff.
      invalidateThreadList()
    },
    [invalidateThreadList, patchThreadSummary]
  )

  const handleThreadSummaryChanged = useCallback(
    (summary: ThreadSummaryResponse, revision: number) => {
      patchThreadSummary(summary.id, summary, revision)
      invalidateThreadList()
    },
    [invalidateThreadList, patchThreadSummary]
  )

  const handleClearFilters = useCallback(() => {
    setSearchParams((current) => {
      const params = clearHistoryFilterParams(current, workspace)
      params.delete("status")
      return params
    })
  }, [setSearchParams, workspace])

  const handleThreadStateChanged = useCallback(
    (threadKey: string, status: ThreadStatus) => {
      if (statusFilter !== "pending" || status === "pending") {
        patchThreadSummary(threadKey, { status })
        invalidateThreadList()
        return
      }

      // This mutation changes membership as well as summaries. Ignore responses
      // fetched before it, while keeping the scheduler's single-flight lifecycle.
      requestGenerationRef.current += 1
      const currentThreads = threads ?? []
      const removedIndex = currentThreads.findIndex(
        (thread) => thread.id === threadKey
      )
      const remainingThreads = currentThreads.filter(
        (thread) => thread.id !== threadKey
      )
      const nextTotalRecords = Math.max(
        0,
        totalRecords - (removedIndex >= 0 ? 1 : 0)
      )
      const nextTotalPages = Math.max(
        1,
        Math.ceil(nextTotalRecords / THREAD_PAGE_LIMIT)
      )
      const nextPage = Math.min(page, nextTotalPages)
      const nextThread =
        removedIndex >= 0
          ? (remainingThreads[removedIndex] ?? remainingThreads.at(-1))
          : remainingThreads[0]

      applyThreadListSnapshot(
        createThreadListPageSnapshot({
          items: remainingThreads,
          totalRecords: nextTotalRecords,
          totalPages: nextTotalPages,
        })
      )
      clearPendingSnapshot()
      setSearchParams((current) => {
        const params = new URLSearchParams(current.toString())
        params.set("page", nextPage.toString())
        if (isMobile || !nextThread || nextPage !== page) {
          return clearThreadSelection(params)
        }
        return selectThread(params, nextThread.id)
      })

      if (nextPage === page) {
        invalidateThreadList()
      }
    },
    [
      applyThreadListSnapshot,
      clearPendingSnapshot,
      isMobile,
      page,
      invalidateThreadList,
      patchThreadSummary,
      setSearchParams,
      statusFilter,
      threads,
      totalRecords,
    ]
  )

  const handleThreadStatusObserved = useCallback(
    (threadKey: string, status: ThreadStatus, revision: number) => {
      // Observing a teammate's change must not run the explicit mutation's
      // next-conversation navigation or close an in-progress reply.
      if (statusFilter !== "pending" || status === "pending") return
      patchThreadSummary(threadKey, { status }, revision)
    },
    [patchThreadSummary, statusFilter]
  )

  const retryAgents = useCallback(() => {
    if (!organizationId) return
    getAgents({
      searchParams: new URLSearchParams({
        oid: organizationId,
        wid: workspace.id,
      }),
    })
  }, [getAgents, organizationId, workspace.id])

  const hasActiveFilters =
    hasActiveHistoryFilters(searchParams, workspace) ||
    statusFilter === "pending"
  const hasHistoricalMatch =
    (agentFilterId !== "" &&
      agentFilterId !== AGENT_FILTER_ALL &&
      agentFilterId !== AGENT_FILTER_NONE) ||
    feedbackFilter !== DEFAULT_FEEDBACK_FILTER
  const hasPagedThreads = Boolean(threads && totalPages > 1)

  const availableDetailPaneWidth =
    (splitPaneWidth ?? viewportWidth) - LIST_MIN_WIDTH - SPLIT_PANE_BORDER_PX
  const minDetailPaneWidth = Math.min(
    DETAIL_MIN_WIDTH,
    Math.max(DETAIL_COMPACT_MIN_WIDTH, availableDetailPaneWidth)
  )
  const maxDetailPaneWidth = Math.max(
    minDetailPaneWidth,
    Math.min(DETAIL_MAX_WIDTH, availableDetailPaneWidth)
  )
  const effectiveDetailPaneWidth = clampDetailWidth(
    detailPaneWidth,
    maxDetailPaneWidth,
    minDetailPaneWidth
  )
  const { isResizing, onResizeStart, onResizeKeyDown } = useDrawerResize({
    width: effectiveDetailPaneWidth,
    onWidthChange: setDetailPaneWidth,
    enabled: !isMobile,
    minWidth: minDetailPaneWidth,
    maxWidth: maxDetailPaneWidth,
  })

  useEffect(() => {
    if (isMobile) return
    const element = splitPaneRef.current
    if (!element) return
    const update = () => setSplitPaneWidth(element.clientWidth)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [isMobile])

  useEffect(() => {
    if (isMobile || isResizing) return
    window.localStorage.setItem(
      HISTORY_DETAIL_WIDTH_STORAGE_KEY,
      effectiveDetailPaneWidth.toString()
    )
  }, [effectiveDetailPaneWidth, isMobile, isResizing])

  const showOnboarding =
    threadsStatus === FetchStatus.Success &&
    threads !== undefined &&
    threads.length === 0 &&
    !hasActiveFilters &&
    !selectedThreadKey &&
    page === 1 &&
    pendingSnapshot?.queryKey !== listQueryKey &&
    threadListPollingFailures < 3

  if (showOnboarding) {
    return (
      <OnboardingEmptyState
        icon={MessageSquare}
        title={t("historyList.onboarding.title")}
        description={t("historyList.onboarding.description")}
      />
    )
  }

  const listBody =
    threadsStatus === FetchStatus.Error && threads === undefined ? (
      <div className="px-4 py-6">
        <OnboardingEmptyState
          icon={AlertCircle}
          title={t("historyList.thread.loadFailed")}
          description={t("historyList.thread.loadFailedDescription")}
          className="py-16"
        />
      </div>
    ) : threads === undefined ? (
      <div className="px-4 py-6">
        <LoadingPage message={t(DEFAULT_LOADING_MESSAGE)} topOffset="16px" />
      </div>
    ) : threads.length === 0 ? (
      <div className="px-4 py-6">
        <OnboardingEmptyState
          icon={MessageSquare}
          title={t("historyList.filteredEmpty.title")}
          description={t("historyList.filteredEmpty.description")}
          secondaryAction={{
            label: t("historyList.filteredEmpty.clearFilters"),
            onClick: handleClearFilters,
          }}
          className="py-16"
        />
      </div>
    ) : (
      <div className="divide-y divide-border/50">
        {threads.map((thread) => (
          <ThreadListItem
            key={thread.id}
            thread={thread}
            selected={!isMobile && selectedThreadKey === thread.id}
            hasHistoricalMatch={hasHistoricalMatch}
            onSelect={handleSelectThread}
          />
        ))}
      </div>
    )

  const hasPendingSnapshot = pendingSnapshot?.queryKey === listQueryKey
  const hasPendingListUpdates = hasPendingSnapshot && !isLiveThreadListQuery
  const hasInvalidPage =
    hasPendingSnapshot && page > pendingSnapshot.snapshot.totalPages
  const isThreadListSyncDelayed = threadListPollingFailures >= 3
  const listSyncStatus =
    hasPendingListUpdates || isThreadListSyncDelayed ? (
      <div
        role="status"
        className={cn(
          "flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3",
          isThreadListSyncDelayed
            ? "border-amber-200 bg-amber-50 dark:border-amber-900/70 dark:bg-amber-950/40"
            : "border-blue-200 bg-blue-50 dark:border-blue-900/70 dark:bg-blue-950/40"
        )}
      >
        <div className="flex min-w-0 flex-1 basis-40 items-center gap-3">
          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
              isThreadListSyncDelayed
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200"
                : "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
            )}
          >
            {isThreadListSyncDelayed ? (
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
            ) : (
              <BellRing className="h-4 w-4" aria-hidden="true" />
            )}
          </span>
          <div className="min-w-0">
            <p
              className={cn(
                "text-sm font-semibold",
                isThreadListSyncDelayed
                  ? "text-amber-950 dark:text-amber-100"
                  : "text-blue-950 dark:text-blue-100"
              )}
            >
              {t(
                hasPendingListUpdates
                  ? hasInvalidPage
                    ? "historyList.thread.pageUnavailable"
                    : "historyList.thread.newActivity"
                  : "historyList.thread.syncDelayed"
              )}
            </p>
            <p
              className={cn(
                "text-xs",
                isThreadListSyncDelayed
                  ? "text-amber-900/80 dark:text-amber-200/80"
                  : "text-blue-800/80 dark:text-blue-200/80"
              )}
            >
              {t(
                hasPendingListUpdates
                  ? hasInvalidPage
                    ? "historyList.thread.pageUnavailableDescription"
                    : "historyList.thread.newActivityDescription"
                  : "historyList.thread.syncDelayedDescription"
              )}
            </p>
            {hasPendingListUpdates && isThreadListSyncDelayed && (
              <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
                <span className="font-medium">
                  {t("historyList.thread.syncDelayed")}
                </span>{" "}
                {t("historyList.thread.syncDelayedDescription")}
              </p>
            )}
          </div>
        </div>
        <TooltipProvider delayDuration={100}>
          <div className="flex shrink-0 items-center gap-1.5">
            {isThreadListSyncDelayed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 border-amber-300 bg-background text-amber-700 hover:bg-amber-100 hover:text-amber-800 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-900"
                    aria-label={t("historyList.thread.retrySync")}
                    disabled={isThreadListPolling}
                    onClick={() => runThreadListPollNow()}
                  >
                    <RefreshCw
                      className={cn(
                        "h-4 w-4",
                        isThreadListPolling && "animate-spin"
                      )}
                      aria-hidden="true"
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("historyList.thread.retrySync")}
                </TooltipContent>
              </Tooltip>
            )}
            {hasPendingListUpdates && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-2 bg-background"
                onClick={handleApplyManualSnapshot}
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                {t(
                  hasInvalidPage
                    ? "historyList.thread.viewAvailablePage"
                    : "historyList.thread.applyUpdates"
                )}
              </Button>
            )}
          </div>
        </TooltipProvider>
      </div>
    ) : null

  const latestThreadsControl =
    (hasPendingSnapshot || unseenLatestThread?.queryKey === listQueryKey) &&
    isLiveThreadListQuery ? (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "pointer-events-none inset-x-0 top-3 z-20 flex justify-center px-4",
          isMobile ? "sticky h-0" : "absolute"
        )}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="pointer-events-auto min-h-11 gap-2 rounded-full border-blue-300 bg-background px-4 text-blue-700 shadow-md hover:bg-background hover:text-blue-800 dark:border-blue-800 dark:bg-background dark:text-blue-200 dark:hover:bg-background"
          onClick={handleApplyManualSnapshot}
        >
          <ArrowUp className="h-4 w-4" aria-hidden="true" />
          {t("historyList.thread.viewLatest")}
        </Button>
      </div>
    ) : null

  const header = (
    <div className="shrink-0 border-b bg-background px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="min-w-0 truncate text-base font-semibold leading-snug tracking-tight text-foreground">
          {t("historyList.title", { count: totalRecords })}
        </h3>
        <div className="flex shrink-0 items-center gap-1.5">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 gap-1.5 px-2.5 text-xs font-medium",
                  activeFilterCount > 0 &&
                    "border-primary/30 bg-primary/5 text-foreground hover:bg-primary/10"
                )}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span>{t("historyList.filters.label")}</span>
                {activeFilterCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 min-w-4 justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-80 space-y-3 p-3"
              sideOffset={6}
            >
              <AgentSelect
                agents={agents}
                currentWorkspaceId={workspace.id}
                onAgentChange={(agentId) =>
                  updateSearchParams({ page: 1, agentId })
                }
                defaultValue={agentFilterId || AGENT_FILTER_ALL}
                compact={true}
                isLoading={agentsStatus === FetchStatus.Loading}
                hasError={Boolean(agentsError)}
                onRetry={retryAgents}
              />
              <ExternalUserIdSelect
                defaultValue={identityMasked ? "" : externalUserId}
                defaultClientUserId={clientUserId}
                onChange={(nextExternalUserId, nextClientUserId) =>
                  updateSearchParams({
                    page: 1,
                    externalUserId: nextExternalUserId,
                    clientUserId: nextClientUserId || "",
                  })
                }
                compact={true}
                disableFreeTextSelection={identityMasked}
                currentWorkspaceId={workspace.id}
                currentOrganizationId={organizationId}
              />
              <SourceSelect
                value={sourceFilter}
                compact={true}
                onSourceChange={(value) =>
                  updateSearchParams({ page: 1, sourceFilter: value })
                }
              />
              <ThreadStatusSelect
                value={statusFilter}
                onChange={(value) =>
                  updateSearchParams({ page: 1, statusFilter: value })
                }
              />
              <HumanModeSelect
                value={humanModeFilter}
                compact={true}
                onHumanModeChange={(value) =>
                  updateSearchParams({ page: 1, humanModeFilter: value })
                }
              />
              <FeedbackSelect
                defaultValue={feedbackFilter}
                compact={true}
                onFeedbackChange={(value) =>
                  updateSearchParams({ page: 1, feedbackFilter: value })
                }
              />
              {activeFilterCount > 0 && (
                <div className="flex justify-end border-t border-border/60 pt-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="h-8 text-xs font-medium"
                  >
                    {t("historyList.filteredEmpty.clearFilters")}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <SortToggle
            value={orderBy}
            iconOnly={true}
            onChange={(value) =>
              updateSearchParams({ page: 1, orderBy: value })
            }
          />
        </div>
      </div>
      <CustomerSearch
        value={customerSearch}
        identityMasked={identityMasked}
        onSearch={(query) =>
          updateSearchParams({ page: 1, customerSearch: query })
        }
      />
    </div>
  )

  const renderPagination = (variant: "mobile" | "desktop") => {
    if (!hasPagedThreads) return null

    return (
      <div
        className={cn(
          "border-t bg-background px-4 py-3",
          variant === "desktop"
            ? "absolute inset-x-0 bottom-0 z-10 shadow-[0_-8px_18px_-16px_hsl(var(--foreground)/0.08)]"
            : "shrink-0"
        )}
      >
        <PageNav
          className="mb-0"
          page={page}
          totalPages={totalPages}
          onPageChange={(nextPage) => updateSearchParams({ page: nextPage })}
        />
      </div>
    )
  }

  if (isMobile && !selectedThreadKey) {
    return (
      <div className="rounded-xl border bg-background">
        {header}
        {listSyncStatus}
        <div className="relative">
          {latestThreadsControl}
          <div ref={listScrollRef} className="overflow-x-hidden">
            <div ref={listTopRef} aria-hidden="true" />
            {listBody}
          </div>
        </div>
        {renderPagination("mobile")}
      </div>
    )
  }

  return (
    <div
      ref={splitPaneRef}
      className={cn(
        "flex h-full min-h-0 overflow-hidden rounded-xl border bg-background",
        isMobile && "min-h-[32rem]"
      )}
    >
      <section
        className={cn(
          "relative h-full min-h-0 min-w-0 flex-1 overflow-hidden border-r bg-background",
          isMobile && "hidden"
        )}
        style={{ minWidth: `${LIST_MIN_WIDTH}px` }}
      >
        <div className="flex h-full min-h-0 flex-col">
          {header}
          {listSyncStatus}
          <div className="relative min-h-0 flex-1">
            <div
              ref={listScrollRef}
              className={cn(
                "h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain",
                hasPagedThreads && "pb-20"
              )}
            >
              <div ref={listTopRef} aria-hidden="true" />
              {listBody}
            </div>
            {latestThreadsControl}
          </div>
        </div>
        {renderPagination("desktop")}
      </section>

      <section
        className={cn(
          "relative h-full min-h-0 shrink-0 overflow-hidden bg-background",
          isResizing && "select-none"
        )}
        style={{
          width: isMobile
            ? "100%"
            : `${crmOpen ? Math.min(effectiveDetailPaneWidth + 320, Math.max(minDetailPaneWidth, availableDetailPaneWidth)) : effectiveDetailPaneWidth}px`,
        }}
      >
        {!isMobile && !crmOpen && (
          <ResizeHandle
            ariaLabel={t("historyList.thread.resizeHandle")}
            width={effectiveDetailPaneWidth}
            minWidth={minDetailPaneWidth}
            maxWidth={maxDetailPaneWidth}
            isResizing={isResizing}
            onResizeStart={onResizeStart}
            onKeyDown={onResizeKeyDown}
          />
        )}
        {selectedThreadKey ? (
          <ThreadDetail
            getThreadSummaryRevision={getThreadSummaryRevision}
            organizationId={organizationId}
            workspace={workspace}
            threadKey={selectedThreadKey}
            readIntentToken={
              readIntent?.threadKey === selectedThreadKey
                ? readIntent.token
                : null
            }
            anchorHistoryId={anchorHistoryId}
            onBack={isMobile ? handleBack : undefined}
            onClearAnchor={handleClearAnchor}
            onThreadSummaryChanged={handleThreadSummaryChanged}
            onThreadListInvalidated={invalidateThreadList}
            onThreadStateChanged={handleThreadStateChanged}
            onThreadStatusObserved={handleThreadStatusObserved}
            onReadStateChanged={handleReadStateChanged}
            onThreadRemoved={handleBack}
            onCrmOpenChange={setCrmOpen}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-5 text-sm text-muted-foreground">
            {t("historyList.thread.selectConversation")}
          </div>
        )}
      </section>
    </div>
  )
}

export default ThreadList
